import { useState, useEffect, useCallback } from "react";
import { Printer, TrendingUp, Banknote, Lock, Unlock } from "lucide-react";
import { api, rd, printCashClose, paymentLabel, askPin } from "./posShared";
import { toast } from "sonner";

export default function CierreCaja({ settings }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/pos/cash-close?date=${date}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const closeCash = async () => {
    if (!window.confirm(`¿Cerrar la caja del ${date}? Podrás reabrirla solo con PIN de administrador.`)) return;
    try { await api.post("/pos/cash-close/close", { date }); toast.success("Caja cerrada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const reopenCash = async () => {
    const pin = await askPin("Ingresa el PIN de administrador o un CÓDIGO DE AUTORIZACIÓN de un solo uso:");
    if (!pin) return;
    const reason = window.prompt("Motivo de la reapertura:") || "";
    try { await api.post("/pos/cash-close/reopen", { date, pin, reason }); toast.success("Caja reabierta"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "PIN incorrecto"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl">Cierre de caja</h1>
          {data?.seller_scope && <p className="text-xs text-rose mt-1">Mostrando solo tus ventas ({data.seller_scope})</p>}
          {data?.closed && <p className="text-xs text-amber-600 mt-1 inline-flex items-center gap-1"><Lock size={12} /> Caja cerrada{data.closed_by ? ` por ${data.closed_by}` : ""}</p>}
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[11px] uppercase text-neutral-500">Fecha</label>
            <input data-testid="pos-cierre-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="block bg-white border border-neutral-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <button data-testid="pos-cierre-print" onClick={() => data && printCashClose(data, settings)} className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition"><Printer size={14} /> Imprimir cierre</button>
          {data && !data.closed && <button data-testid="pos-cierre-close" onClick={closeCash} className="inline-flex items-center gap-2 border border-espresso text-espresso rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-espresso hover:text-cream transition"><Lock size={14} /> Cerrar caja</button>}
          {data && data.closed && <button data-testid="pos-cierre-reopen" onClick={reopenCash} className="inline-flex items-center gap-2 border border-amber-400 text-amber-600 rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-amber-50 transition"><Unlock size={14} /> Reabrir (PIN)</button>}
        </div>
      </div>

      {data ? (
        <div data-testid="pos-cierre-content">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat l="Venta total" v={rd(data.total_sales)} accent />
            <Stat l="Facturas" v={data.invoice_count} />
            <Stat l="Unidades vendidas" v={data.units_sold} />
            <Stat l="Efectivo en caja" v={rd(data.cash_received)} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <h2 className="font-medium mb-3 flex items-center gap-2"><Banknote size={18} className="text-rose" /> Ventas por método de pago</h2>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase text-neutral-500 border-b border-neutral-100"><th className="py-2">Método</th><th className="py-2 text-center">#</th><th className="py-2 text-right">Total</th></tr></thead>
                <tbody>
                  {data.by_payment_method.length ? data.by_payment_method.map((m) => (
                    <tr key={m.method} className="border-b border-neutral-50">
                      <td className="py-2">{paymentLabel(m.method)}</td>
                      <td className="py-2 text-center">{m.count}</td>
                      <td className="py-2 text-right font-medium">{rd(m.total)}</td>
                    </tr>
                  )) : <tr><td colSpan={3} className="py-6 text-center text-neutral-400">Sin ventas este día.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <h2 className="font-medium mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-rose" /> Resumen del día</h2>
              <div className="space-y-1">
                <Line l="Descuentos otorgados" v={rd(data.total_discount)} />
                <Line l="ITBIS cobrado" v={rd(data.total_itbis)} />
                {data.delivery_total > 0 && <Line l="Cobrado por delivery" v={rd(data.delivery_total)} />}
                {data.estimated_profit != null && <Line l="Ganancia estimada" v={rd(data.estimated_profit)} />}
                <Line l="Pendiente de cobro" v={rd(data.pending_balance)} />
                <Line l="Facturas anuladas" v={data.voided_count} />
                {data.offline_synced_count > 0 && (
                  <Line l="Ventas offline sincronizadas" v={data.offline_synced_count} />
                )}
                <Line l="Rango de facturas" v={`${data.first_invoice || "—"} → ${data.last_invoice || "—"}`} />
              </div>
            </div>
          </div>
        </div>
      ) : <p className="text-neutral-400">Cargando...</p>}
    </div>
  );
}

const Stat = ({ l, v, accent }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4">
    <p className="text-[11px] uppercase tracking-wider text-neutral-500">{l}</p>
    <p className={`text-2xl font-serif mt-1 ${accent ? "text-emerald-600" : "text-espresso"}`}>{v}</p>
  </div>
);
const Line = ({ l, v }) => <div className="flex justify-between text-sm border-b border-neutral-50 py-1.5"><span className="text-neutral-600">{l}</span><span className="font-medium">{v}</span></div>;
